import { supabase } from './supabase';
import { format, subDays, eachDayOfInterval } from 'date-fns';

// ── Types ───────────────────────────────────────────────────

export interface DSRTrendPoint {
  date: string; // 'yyyy-MM-dd'
  dsr_count: number;
  submitted_count: number;
  high_risk_count: number;
  medium_risk_count: number;
  low_risk_count: number;
}

export interface SafetyEventTrendPoint {
  date: string;
  near_miss_count: number;
  first_aid_count: number;
  recordable_count: number;
  lti_count: number;
  good_catch_count: number;
}

export interface SSETrendPoint {
  date: string;
  total_pob: number;
  sse_count: number;
  sse_percentage: number;
}

export interface ActivityRiskBreakdown {
  high_count: number;
  medium_count: number;
  low_count: number;
  top_risk_tags: string[];
}

export interface SafetyEventSummary {
  near_miss: number;
  first_aid: number;
  recordable: number;
  lti: number;
  property_damage: number;
  environmental_release: number;
}

export interface TopActionItem {
  id: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date: string | null;
  source_section: string | null;
  dsr_report_date: string | null;
  project_name: string | null;
}

// ── Helpers ─────────────────────────────────────────────────

const dayStr = (d: Date) => format(d, 'yyyy-MM-dd');

function daysBack(days: number): { start: Date; days: string[] } {
  const end = new Date();
  const start = subDays(end, days - 1);
  const range = eachDayOfInterval({ start, end });
  return { start, days: range.map(dayStr) };
}

const SUBMITTED_STATUSES = ['submitted', 'reviewed', 'flagged', 'amended'];

// Risk source-of-truth (post-ARCH-030): dsr_reports.overall_risk_level — the
// composite computed by the risk-score Edge Function, honoring any SSR
// override. For legacy DSRs that predate the composite column we fall back
// to dsr_activities_summaries.overall_risk_level (the activity-only roll-up
// that drove analytics during Crawl).
export async function getDSRTrend(
  projectIds: string[],
  days = 30,
): Promise<DSRTrendPoint[]> {
  const { start, days: dayList } = daysBack(days);
  if (projectIds.length === 0) return dayList.map(emptyDSRPoint);

  const { data, error } = await supabase
    .from('dsr_reports')
    .select(
      'id, report_date, status, project_id, overall_risk_level, ' +
      'dsr_activities_summaries(overall_risk_level)'
    )
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', dayStr(start));
  if (error) throw error;

  const buckets = new Map<string, DSRTrendPoint>();
  dayList.forEach((d) => buckets.set(d, emptyDSRPoint(d)));

  for (const row of (data ?? []) as any[]) {
    const b = buckets.get(row.report_date);
    if (!b) continue;
    b.dsr_count += 1;
    if (SUBMITTED_STATUSES.includes(row.status)) b.submitted_count += 1;
    const summary = (row.dsr_activities_summaries ?? [])[0];
    const risk = row.overall_risk_level ?? summary?.overall_risk_level ?? null;
    if (risk === 'high' || risk === 'critical') b.high_risk_count += 1;
    else if (risk === 'medium') b.medium_risk_count += 1;
    else if (risk === 'low') b.low_risk_count += 1;
  }
  return dayList.map((d) => buckets.get(d)!);
}

function emptyDSRPoint(date: string): DSRTrendPoint {
  return {
    date,
    dsr_count: 0,
    submitted_count: 0,
    high_risk_count: 0,
    medium_risk_count: 0,
    low_risk_count: 0,
  };
}

export async function getSafetyEventTrend(
  projectIds: string[],
  days = 30,
): Promise<SafetyEventTrendPoint[]> {
  const { start, days: dayList } = daysBack(days);
  if (projectIds.length === 0) return dayList.map(emptySafetyPoint);

  // Fetch DSR id → date map for the window so we can bucket by report_date.
  const { data: dsrs, error: dsrErr } = await supabase
    .from('dsr_reports')
    .select('id, report_date')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', dayStr(start));
  if (dsrErr) throw dsrErr;

  const dsrIds = (dsrs ?? []).map((d) => d.id);
  const dsrDate = new Map<string, string>();
  (dsrs ?? []).forEach((d) => dsrDate.set(d.id, d.report_date));

  const buckets = new Map<string, SafetyEventTrendPoint>();
  dayList.forEach((d) => buckets.set(d, emptySafetyPoint(d)));

  if (dsrIds.length === 0) return dayList.map((d) => buckets.get(d)!);

  const [eventsRes, obsRes] = await Promise.all([
    supabase
      .from('dsr_safety_events')
      .select('event_type, dsr_report_id')
      .eq('is_deleted', false)
      .in('dsr_report_id', dsrIds),
    supabase
      .from('dsr_hse_observations')
      .select('observation_type, dsr_report_id')
      .eq('is_deleted', false)
      .eq('observation_type', 'positive')
      .in('dsr_report_id', dsrIds),
  ]);
  if (eventsRes.error) throw eventsRes.error;
  if (obsRes.error) throw obsRes.error;

  for (const e of eventsRes.data ?? []) {
    const d = dsrDate.get(e.dsr_report_id);
    const b = d ? buckets.get(d) : undefined;
    if (!b) continue;
    if (e.event_type === 'near_miss') b.near_miss_count += 1;
    else if (e.event_type === 'first_aid') b.first_aid_count += 1;
    else if (e.event_type === 'recordable_injury') b.recordable_count += 1;
    else if (e.event_type === 'lost_time_injury') b.lti_count += 1;
  }
  for (const o of obsRes.data ?? []) {
    const d = dsrDate.get(o.dsr_report_id);
    const b = d ? buckets.get(d) : undefined;
    if (b) b.good_catch_count += 1;
  }
  return dayList.map((d) => buckets.get(d)!);
}

function emptySafetyPoint(date: string): SafetyEventTrendPoint {
  return {
    date,
    near_miss_count: 0,
    first_aid_count: 0,
    recordable_count: 0,
    lti_count: 0,
    good_catch_count: 0,
  };
}

export async function getSSETrend(
  projectIds: string[],
  days = 30,
): Promise<SSETrendPoint[]> {
  const { start } = daysBack(days);
  if (projectIds.length === 0) return [];

  const { data: manifests, error: mErr } = await supabase
    .from('pob_manifests')
    .select('id, manifest_date')
    .eq('is_deleted', false)
    .eq('status', 'published')
    .in('project_id', projectIds)
    .gte('manifest_date', dayStr(start))
    .order('manifest_date');
  if (mErr) throw mErr;

  const manifestRows = manifests ?? [];
  if (manifestRows.length === 0) return [];

  const manifestIds = manifestRows.map((m) => m.id);
  const { data: entries, error: eErr } = await supabase
    .from('pob_entries')
    .select('manifest_id, is_sse')
    .eq('is_deleted', false)
    .in('manifest_id', manifestIds);
  if (eErr) throw eErr;

  // Aggregate per manifest_date (sum across multiple projects on the same day).
  const byDate = new Map<string, { total: number; sse: number }>();
  const manifestToDate = new Map<string, string>();
  manifestRows.forEach((m) => manifestToDate.set(m.id, m.manifest_date));
  for (const e of entries ?? []) {
    const date = manifestToDate.get(e.manifest_id);
    if (!date) continue;
    const cur = byDate.get(date) ?? { total: 0, sse: 0 };
    cur.total += 1;
    if (e.is_sse) cur.sse += 1;
    byDate.set(date, cur);
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, agg]) => ({
      date,
      total_pob: agg.total,
      sse_count: agg.sse,
      sse_percentage: agg.total > 0 ? Math.round((agg.sse / agg.total) * 1000) / 10 : 0,
    }));
}

export async function getActivityRiskBreakdown(
  projectIds: string[],
  days = 30,
): Promise<ActivityRiskBreakdown> {
  const empty: ActivityRiskBreakdown = {
    high_count: 0, medium_count: 0, low_count: 0, top_risk_tags: [],
  };
  if (projectIds.length === 0) return empty;
  const { start } = daysBack(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', dayStr(start));
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  if (dsrIds.length === 0) return empty;

  const { data, error } = await supabase
    .from('dsr_activities')
    .select('risk_level, risk_tags')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds);
  if (error) throw error;

  const tagCounts = new Map<string, number>();
  let high = 0, medium = 0, low = 0;
  for (const a of (data ?? []) as any[]) {
    if (a.risk_level === 'high' || a.risk_level === 'critical') high += 1;
    else if (a.risk_level === 'medium') medium += 1;
    else low += 1;
    for (const tag of (a.risk_tags ?? []) as string[]) {
      tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
    }
  }
  const top_risk_tags = Array.from(tagCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([t]) => t);
  return { high_count: high, medium_count: medium, low_count: low, top_risk_tags };
}

export async function getSafetyEventSummary(
  projectIds: string[],
  days = 30,
): Promise<SafetyEventSummary> {
  const empty: SafetyEventSummary = {
    near_miss: 0, first_aid: 0, recordable: 0, lti: 0,
    property_damage: 0, environmental_release: 0,
  };
  if (projectIds.length === 0) return empty;
  const { start } = daysBack(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', dayStr(start));
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  if (dsrIds.length === 0) return empty;

  const { data, error } = await supabase
    .from('dsr_safety_events')
    .select('event_type')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds);
  if (error) throw error;

  const result = { ...empty };
  for (const e of (data ?? []) as { event_type: string }[]) {
    if (e.event_type === 'near_miss') result.near_miss += 1;
    else if (e.event_type === 'first_aid') result.first_aid += 1;
    else if (e.event_type === 'recordable_injury') result.recordable += 1;
    else if (e.event_type === 'lost_time_injury') result.lti += 1;
    else if (e.event_type === 'property_damage') result.property_damage += 1;
    else if (e.event_type === 'environmental_release') result.environmental_release += 1;
  }
  return result;
}

export async function getTopActionItems(
  projectIds: string[],
  limit = 5,
): Promise<TopActionItem[]> {
  if (projectIds.length === 0) return [];

  // Need DSR id → (report_date, project_name) map. Pull DSRs in scope first.
  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id, report_date, projects!inner(name)')
    .eq('is_deleted', false)
    .in('project_id', projectIds);
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  if (dsrIds.length === 0) return [];
  const dsrMeta = new Map<string, { date: string; project: string }>();
  for (const d of (dsrs ?? []) as any[]) {
    dsrMeta.set(d.id, { date: d.report_date, project: d.projects?.name ?? '—' });
  }

  // Postgres orders enum by declaration order (low,medium,high,critical),
  // so DESC = critical→high→medium→low. nullsLast for due_date keeps undated
  // items at the bottom.
  const { data, error } = await supabase
    .from('dsr_action_items')
    .select('id, description, priority, due_date, source_section, dsr_report_id, status')
    .eq('is_deleted', false)
    .in('status', ['open', 'in_progress', 'overdue'])
    .in('dsr_report_id', dsrIds)
    .order('priority', { ascending: false })
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(limit);
  if (error) throw error;

  return ((data ?? []) as any[]).map((r) => {
    const meta = dsrMeta.get(r.dsr_report_id);
    return {
      id: r.id,
      description: r.description,
      priority: r.priority,
      due_date: r.due_date,
      source_section: r.source_section,
      dsr_report_date: meta?.date ?? null,
      project_name: meta?.project ?? null,
    };
  });
}

// ── Helpers shared by dashboards ─────────────────────────────

export async function getProjectIdsForTenant(tenantId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('id')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false);
  if (error) throw error;
  return (data ?? []).map((p) => p.id);
}

export async function getProjectIdsForSupervisor(
  _userId: string,
  tenantId: string,
): Promise<string[]> {
  // RLS already filters projects to the supervisor's assigned scope on read.
  // For Walk phase we treat supervisor scope = all readable projects in the
  // tenant. Per-supervisor filtering via user_scope_assignments lands in P1.
  return getProjectIdsForTenant(tenantId);
}
